import { Formik, Field } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { Link, FormGroup, Stack, TextInput, Checkbox, PasswordInput, Button, InlineNotification } from '@carbon/react';
import { ArrowRight } from '@carbon/react/icons';
import SocialButtons from 'src/components/Auth/SocialButtons';
import { useAuthActions } from 'src/hooks/useAuthActions';
import { authClient } from 'src/services/auth/client';

interface SignInProps {}

const Schema = z.object({
  username: z.string().max(60).trim(),
  password: z.string().max(60).trim(),
});

const SignIn = React.memo((_: SignInProps) => {
  const { t } = useTranslation('authentication');
  const { signIn, signInSocial, isPending, error } = useAuthActions();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  async function handleSubmit(values: { username: string; password: string }) {
    const result = await signIn(values.username, values.password);
    if (result) {
      // A returning member may have memberships but no active organization yet (the session
      // starts with activeOrganizationId null). Activate the first one before navigating so
      // the portal's access token carries organization claims on its very first mint —
      // otherwise the app has no path into an existing org (ContextLoader bounces to the
      // onboarding wizard, and the org switcher is unreachable there).
      const { data: orgs } = await authClient.organization.list();
      if (orgs && orgs.length > 0) {
        await authClient.organization.setActive({ organizationId: orgs[0].id });
      }
      // Authoritative session read for EVERY sign-in (also for users with no memberships
      // yet, e.g. an invitee accepting an invitation): the first getSession after the
      // sign-in response can otherwise answer from a stale state and hold a null session
      // — the app then shows the signed-out view instead of the invitation.
      await authClient.getSession({ query: { disableCookieCache: true } });
      navigate(searchParams.get('returnTo') || '/', { replace: true });
    }
  }

  return (
    <div className={'sign-in__container'}>
      <h4>{t('signIn.title')}</h4>
      <p>
        {t('signIn.troubleToSignIn')} <Link>{t('signIn.contactUs')}.</Link>
      </p>
      {error && <InlineNotification subtitle={t('errors.invalidEmailOrUsername')} />}
      <Formik
        validationSchema={toFormikValidationSchema(Schema)}
        initialValues={{
          username: '',
          password: '',
          rememberMe: false,
        }}
        onSubmit={async (values) => handleSubmit(values)}
      >
        {({ handleSubmit, values, setFieldValue, isValid }) => (
          // oxlint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
          <form
            onSubmit={handleSubmit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSubmit();
              }
            }}
          >
            <FormGroup className={'sign-in__form'} legendText={''}>
              <Stack gap={5}>
                <Field name="username">
                  {({ field, meta }) => {
                    return (
                      <TextInput
                        id="username"
                        labelText={t('signIn.emailOrUsername')}
                        invalid={!!(meta.touched && meta.error)}
                        invalidText={meta.error}
                        {...field}
                      />
                    );
                  }}
                </Field>
                <Field name="password">
                  {({ field, meta }) => {
                    return (
                      <PasswordInput
                        id="password"
                        labelText={t('signIn.password')}
                        invalid={!!(meta.touched && meta.error)}
                        invalidText={meta.error}
                        {...field}
                      />
                    );
                  }}
                </Field>
                <div className="sign-in__options">
                  <Checkbox
                    labelText={t('signIn.rememberMe')}
                    checked={values.rememberMe}
                    onChange={(e, { checked }) => setFieldValue('rememberMe', checked)}
                    id="checkbox-label-1"
                  />
                  <Link>{t('signIn.forgotPassword')}</Link>
                </div>
                <Button renderIcon={ArrowRight} disabled={!isValid || isPending} onClick={handleSubmit} type="submit">
                  {t('signIn.submitButtonText')}
                </Button>
                <div className="cds--auth__links">
                  <RouterLink to="/forgot-password">{t('authentication:signIn.forgotPassword')}</RouterLink>
                  <RouterLink to="/sign-up">{t('authentication:signIn.createAccount')}</RouterLink>
                </div>
              </Stack>
            </FormGroup>
            <SocialButtons onSelect={signInSocial} />
          </form>
        )}
      </Formik>
    </div>
  );
});

export default SignIn;
